import { useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import { NumberFormatCustom } from "@vendor/examples/NumberFormat";
import MDButton from "@vendor/components/MDButton";

import { addPreInvoiceItem } from "../api/preInvoiceApi";
import { generateInvoice } from "../lib/invoiceFlow";
import { computeLineTotal } from "../lib/preInvoiceRules";

import type { TFunction } from "i18next";
import type { ChangeEvent } from "react";
import type { PreInvoiceDetail } from "../api/preInvoiceApi";

/** Linea que el usuario carga a mano en el dialogo de conceptos adicionales. */
interface DetailInput {
  description: string;
  units: number;
  priceUnit: number;
  total: number;
  [key: string]: string | number;
}

export interface AddDetailProps {
  thisLoading: () => void;
  notLoading: () => void;
  value: boolean;
  onDisabled: () => void;
  /** Los cuatro solo aplican al modo que postea; en "modo colecta" no se pasan. */
  onActive?: () => void;
  urlAddItem?: string;
  urlPreInvoicePending?: string;
  urlConfirmPreInvoice?: string;
  onCollect?: ((items: DetailInput[]) => void) | null;
  t: TFunction;
}

export const AddDetail = ({
  thisLoading,
  notLoading,
  value,
  onDisabled,
  onActive,
  urlAddItem,
  urlPreInvoicePending,
  urlConfirmPreInvoice,
  onCollect,
  t,
}: AddDetailProps) => {
  const stateInitial = { description: "", units: 1, priceUnit: 0, total: 0 };
  const [inputFields, setInputFields] = useState<DetailInput[]>([stateInitial]);
  const [errors, setErrors] = useState<{ error: boolean; messageList: string[] }>({ error: false, messageList: [] });

  const handleFormChange = (index: number, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const data = [...inputFields];
    // `index` sale del `map` que renderiza `inputFields`, asi que la fila existe.
    const row = data[index]!;
    row[event.target.name] = event.target.value;
    if (event.target.name === "units") {
      row["total"] = computeLineTotal(event.target.value, row["priceUnit"]);
    }
    if (event.target.name === "priceUnit") {
      row["total"] = computeLineTotal(row["units"], event.target.value);
    }

    setInputFields(data);
  };

  const addFields = () => {
    setInputFields([...inputFields, stateInitial]);
  };

  const removeFields = (index: number) => {
    const data = [...inputFields];
    data.splice(index, 1);
    setInputFields(data);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validation(inputFields)) {
      notLoading();
      return;
    }
    // Camino A (colaborador, mes actual): los conceptos NO se postean a una prefactura
    // persistida (que aún no existe). Se juntan en el estado del llamador y viajan al confirmar.
    if (typeof onCollect === "function") {
      onCollect(inputFields);
      setInputFields([stateInitial]);
      return;
    }
    thisLoading();
    onDisabled();
    const request = {
      preInvoiceDetails: inputFields,
    };
    addPreInvoiceItem(urlAddItem as string, request).then((data) => {
      setInputFields([stateInitial]);
      generateInvoice(
        data,
        thisLoading,
        notLoading,
        onActive as () => void,
        urlPreInvoicePending as string,
        urlConfirmPreInvoice as string,
        undefined
      );
    });
  };

  const validation = (data: DetailInput[]) => {
    const messages: string[] = [];
    let error = false;
    data.map((element: DetailInput) => {
      if (element.description === "") {
        messages.push(t("descriptionNotEmpty"));
        error = true;
      }
      if (String(element.priceUnit) === "") {
        messages.push(t("priceUnitsNotEmpty"));
        error = true;
      }
      if (String(element.total) === "") {
        messages.push(t("totalNotEmpty"));
        error = true;
      }
    });
    setErrors({ error, messageList: messages });
    return error;
  };

  return (
    <Dialog open={value} maxWidth="xl" onClose={onDisabled}>
      <form onSubmit={submit}>
        <DialogTitle>{t("addAdditionalConcept")}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid size={12}>
              {inputFields.map((input, index) => {
                return (
                  <Box key={index} sx={{ mt: 1 }}>
                    <FormControl>
                      <TextField
                        required
                        value={input.description}
                        name="description"
                        id="component-outlined"
                        defaultValue="Composed TextField"
                        label={t("description")}
                        onChange={(event) => handleFormChange(index, event)}
                      />
                    </FormControl>
                    <FormControl sx={{ ml: 2 }}>
                      <TextField
                        required
                        value={input.units}
                        name="units"
                        id="component-outlined"
                        defaultValue="1"
                        type="number"
                        label={t("units")}
                        onChange={(event) => handleFormChange(index, event)}
                      />
                    </FormControl>
                    <FormControl sx={{ ml: 2 }}>
                      <TextField
                        required
                        value={input.priceUnit}
                        name="priceUnit"
                        id="component-outlined"
                        defaultValue="0"
                        slotProps={{
                          input: { inputComponent: NumberFormatCustom },
                        }}
                        label={t("unitPrice")}
                        onChange={(event) => handleFormChange(index, event)}
                      />
                    </FormControl>
                    <FormControl sx={{ ml: 2, mr: 1 }}>
                      <TextField
                        required
                        value={input.total}
                        name="total"
                        id="component-outlined"
                        defaultValue="0"
                        label={t("total")}
                        slotProps={{
                          input: { inputComponent: NumberFormatCustom, readOnly: true },
                        }}
                        onChange={(event) => handleFormChange(index, event)}
                      />
                    </FormControl>
                    <IconButton
                      color="error"
                      onClick={() => removeFields(index)}
                      aria-label="delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                );
              })}
            </Grid>
            <Grid size={12}>
              {errors.error && (
                <>
                  {" "}
                  <b style={{ color: "red" }}>Error: </b>
                  {errors.messageList.map((message) => (
                    <p style={{ fontSize: 12, color: "red" }}>{message}</p>
                  ))}
                </>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton variant="contained" color="success" onClick={addFields}>
            {t("addMore")}
          </MDButton>
          <MDButton onClick={submit} color="info">
            {t("register")}
          </MDButton>
          <MDButton onClick={onDisabled} color="error">
            {t("cancel")}
          </MDButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
